import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/** 歌詞を再生に追従させる粒度。データ側が対応していない場合は表示時に落とし込まれる */
export type LyricsSyncMode = 'word' | 'line' | 'none'

export type PdfTheme = 'light' | 'sepia' | 'dark'

/** AI の重要文の重要度（1: 核心 / 2: 重要 / 3: 補足） */
export type SkimLevel = 1 | 2 | 3

type UISettingsState = {
  episodeGridColumns: 2 | 3
  setEpisodeGridColumns: (columns: 2 | 3) => void
  episodeAspectRatio: 'landscape' | 'portrait'
  setEpisodeAspectRatio: (ratio: 'landscape' | 'portrait') => void
  productAspectRatio: 'landscape' | 'portrait'
  setProductAspectRatio: (ratio: 'landscape' | 'portrait') => void
  isInfiniteScroll: boolean
  setIsInfiniteScroll: (enabled: boolean) => void
  /** リーダーのフッターを常に表示する（コンテンツ領域はフッター分だけ縮む） */
  isReaderFooterPinned: boolean
  setReaderFooterPinned: (pinned: boolean) => void
  /** PDF リーダーの配色 */
  pdfTheme: PdfTheme
  setPdfTheme: (theme: PdfTheme) => void
  /** PDF リーダーの暖色フィルターの強さ（0〜100） */
  pdfWarmth: number
  setPdfWarmth: (warmth: number) => void
  /** PDF リーダーで進行方向と交差する向き（横スクロールなら上下）のスクロールを止める */
  pdfScrollLock: boolean
  setPdfScrollLock: (locked: boolean) => void
  /** 固定した位置（スクロールできる幅に対する比率 0〜1） */
  pdfScrollLockRatio: number
  setPdfScrollLockRatio: (ratio: number) => void
  /** ページめくりで次のページへ動かすときにアニメーションするか（PDF・コミック共通） */
  pageTurnAnimation: boolean
  setPageTurnAnimation: (enabled: boolean) => void
  /** AI 読書アシスト: 流し読みモード（章の重要文を本文中で強調する） */
  readerSkimMode: boolean
  setReaderSkimMode: (enabled: boolean) => void
  /** AI 読書アシスト: 流し読みで出す重要度の下限（1: 核心だけ / 2: 重要以上 / 3: すべて） */
  readerSkimLevel: SkimLevel
  setReaderSkimLevel: (level: SkimLevel) => void
  /** AI 読書アシスト: 章に入ったときに「この章のポイント」を出す */
  readerSectionPreview: boolean
  setReaderSectionPreview: (enabled: boolean) => void
  /** AI 読書アシスト: 人物・用語は読んだ所までのものだけを出す（ネタバレ防止）。オフなら本全体 */
  glossarySpoilerGuard: boolean
  setGlossarySpoilerGuard: (enabled: boolean) => void
  /** AI 読書アシスト: チャットは読んだ所までの本文だけで答える（ネタバレ防止） */
  bookChatSpoilerGuard: boolean
  setBookChatSpoilerGuard: (enabled: boolean) => void
  /** AI 読書アシスト: 質問をこの本ではなく本棚全体（AI 解析済みのすべての本）にする */
  bookChatLibraryMode: boolean
  setBookChatLibraryMode: (enabled: boolean) => void
  /** AI 読書アシスト: 読む目的に関係する箇所に印を付ける（本文の余白と PDF のシークバー） */
  readerPurposeMarks: boolean
  setReaderPurposeMarks: (enabled: boolean) => void
  /** 動画再生時に優先する画質（'original' | '720p' | '480p'） */
  videoQuality: string
  setVideoQuality: (quality: string) => void
  /** ダウンロード時に優先する画質（'original' | '720p' | '480p'） */
  downloadQuality: string
  setDownloadQuality: (quality: string) => void
  /** 歌詞表示の同期粒度（文字 / 行 / なし） */
  lyricsSyncMode: LyricsSyncMode
  setLyricsSyncMode: (mode: LyricsSyncMode) => void
  productScrollPositions: Record<string, number>
  getProductScrollPosition: (domain: string, page: number) => number
  setProductScrollPosition: (domain: string, page: number, position: number) => void
  clearProductScrollPosition: (domain: string, page: number) => void
}

export const useUISettingsStore = create<UISettingsState>()(
  persist(
    (set, get) => ({
      episodeGridColumns: 3,
      setEpisodeGridColumns: (columns) => set({ episodeGridColumns: columns }),
      episodeAspectRatio: 'landscape',
      setEpisodeAspectRatio: (ratio) => set({ episodeAspectRatio: ratio }),
      productAspectRatio: 'portrait',
      setProductAspectRatio: (ratio) => set({ productAspectRatio: ratio }),
      isInfiniteScroll: false,
      setIsInfiniteScroll: (enabled) => set({ isInfiniteScroll: enabled }),
      isReaderFooterPinned: false,
      setReaderFooterPinned: (pinned) => set({ isReaderFooterPinned: pinned }),
      pdfTheme: 'light',
      setPdfTheme: (theme) => set({ pdfTheme: theme }),
      pdfWarmth: 0,
      setPdfWarmth: (warmth) => set({ pdfWarmth: warmth }),
      pdfScrollLock: false,
      setPdfScrollLock: (locked) => set({ pdfScrollLock: locked }),
      pdfScrollLockRatio: 0.5,
      setPdfScrollLockRatio: (ratio) => set({ pdfScrollLockRatio: ratio }),
      pageTurnAnimation: true,
      setPageTurnAnimation: (enabled) => set({ pageTurnAnimation: enabled }),
      readerSkimMode: false,
      setReaderSkimMode: (enabled) => set({ readerSkimMode: enabled }),
      readerSkimLevel: 3,
      setReaderSkimLevel: (level) => set({ readerSkimLevel: level }),
      readerSectionPreview: true,
      setReaderSectionPreview: (enabled) => set({ readerSectionPreview: enabled }),
      glossarySpoilerGuard: true,
      setGlossarySpoilerGuard: (enabled) => set({ glossarySpoilerGuard: enabled }),
      bookChatSpoilerGuard: true,
      setBookChatSpoilerGuard: (enabled) => set({ bookChatSpoilerGuard: enabled }),
      bookChatLibraryMode: false,
      setBookChatLibraryMode: (enabled) => set({ bookChatLibraryMode: enabled }),
      readerPurposeMarks: true,
      setReaderPurposeMarks: (enabled) => set({ readerPurposeMarks: enabled }),
      videoQuality: 'original',
      setVideoQuality: (quality) => set({ videoQuality: quality }),
      downloadQuality: 'original',
      setDownloadQuality: (quality) => set({ downloadQuality: quality }),
      lyricsSyncMode: 'word',
      setLyricsSyncMode: (mode) => set({ lyricsSyncMode: mode }),
      productScrollPositions: {},
      getProductScrollPosition: (domain: string, page: number) => {
        const key = `${domain}-${page}`
        return get().productScrollPositions[key] || 0
      },
      setProductScrollPosition: (domain: string, page: number, position: number) => {
        const key = `${domain}-${page}`
        set((state) => ({
          productScrollPositions: { ...state.productScrollPositions, [key]: position },
        }))
      },
      clearProductScrollPosition: (domain: string, page: number) => {
        const key = `${domain}-${page}`
        set((state) => {
          const newPositions = { ...state.productScrollPositions }
          delete newPositions[key]
          return { productScrollPositions: newPositions }
        })
      },
    }),
    {
      name: 'ui-settings',
    },
  ),
)
